import {api} from "../../config/api";
import secureStorage from "../../utils/secureStorage";
import {parseServerDate, parseTaxRate} from "./posMath";

// Caisse web — mêmes endpoints que la caisse mobile (pos_clone
// services/DataSyncService.js et BackgroundSyncService.js) :
//   GET  /getCatalogue           catalogue complet (produits, remises, clients…)
//   POST /journal/sync           ouverture / clôture de la session de caisse
//   POST /sale/sync              la vente (upsert par id)
//   POST /sale_items/sync        une ligne
//   POST /sale_discounts/sync    une remise de ligne
//   POST /sale_payments/sync     un paiement
// Les /…/sync attendent du multipart (FormData), un id UUID généré ici.

// Nom de caisse du journal web : permet de retrouver la session ouverte après
// un rechargement, et de la distinguer des tablettes dans Activité > Journaux.
export const WEB_REGISTER_NAME = "Caisse Web";
export const SALE_STATUS_DONE = "terminer";

const accountHeaders = async (extra = {}) => {
    const account = await secureStorage.getItem("selectedAccount");
    return {"X-account-id": account?.id, Accept: "application/json", ...extra};
};

export const apiErrorMessage = (error, fallback) =>
    error?.response?.data?.error ||
    error?.response?.data?.message ||
    error?.response?.data?.detail ||
    fallback;

export const newId = () =>
    (typeof crypto !== "undefined" && crypto.randomUUID)
        ? crypto.randomUUID()
        : "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
            const r = (Math.random() * 16) | 0;
            return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
        });

const toFormData = (fields) => {
    const fd = new FormData();
    Object.entries(fields).forEach(([key, value]) => {
        if (value !== null && value !== undefined) fd.append(key, String(value));
    });
    return fd;
};

const postSync = async (endpoint, fields) => {
    const headers = await accountHeaders({"Content-Type": "multipart/form-data"});
    return api.post(endpoint, toFormData(fields), {headers});
};

// ── Catalogue ───────────────────────────────────────────────────────────────

const idOf = (...candidates) => candidates.find((c) => c) || null;

const productTaxId = (p) =>
    idOf(p?.taxes?.id, p?.taxesId, p?.taxe?.id, p?.taxeId, p?.tax?.id);

// Réponse brute de /getCatalogue → structures prêtes pour l'écran.
export const normalizeCatalogue = (data = {}) => {
    const taxRateById = new Map((data.taxes || []).map((t) => [t.id, parseTaxRate(t.value)]));
    const rateFor = (taxId) => (taxId ? taxRateById.get(taxId) || 0 : 0);

    const categories = (data.categories || [])
        .filter((c) => c.id && c.active !== false)
        .sort((a, b) => (a.pos_order || 0) - (b.pos_order || 0) || String(a.name).localeCompare(String(b.name)))
        .map((c) => ({id: c.id, name: c.name, color: c.color || null, image: c.image || null}));

    const seen = new Set();
    const products = [];
    (data.products || []).forEach((p) => {
        if (!p.id || seen.has(p.id) || p.active === false || p.isVendable === false) return;
        seen.add(p.id);
        const taxId = productTaxId(p);
        const variants = (p.variants || [])
            .filter((v) => v.id && v.active !== false && v.isVendable !== false)
            .map((v) => {
                const vTax = productTaxId(v) || taxId;
                return {
                    id: v.id,
                    name: v.name,
                    price: Number(v.price) || 0,
                    barcode: v.barcode || "",
                    taxRate: rateFor(vTax),
                    isStockable: v.isStockable,
                    isVendable: v.isVendable,
                };
            });
        products.push({
            id: p.id,
            name: p.name,
            price: Number(p.price) || 0,
            barcode: p.barcode || "",
            color: p.color || null,
            image: p.image || null,
            categoryId: idOf(p.category?.id, p.categoryId),
            taxRate: rateFor(taxId),
            isStockable: p.isStockable,
            isVendable: p.isVendable,
            variants,
        });
    });
    products.sort((a, b) => String(a.name).localeCompare(String(b.name)));

    const payments = (data.payments || [])
        .filter((p) => p.id && p.active !== false)
        .map((p) => ({id: p.id, name: p.name, icon: p.icon}));

    const discounts = (data.discounts || []).filter((d) => d.id && d.active !== false);

    const clients = (data.clients || [])
        .filter((c) => c.id && c.active !== false)
        .map((c) => ({
            id: c.id,
            name: [c.firstName, c.lastName].filter(Boolean).join(" ").trim() || c.phone || "Sans nom",
            phone: c.phone || "",
            email: c.email || "",
            clientTypeId: c.clientType?.id || null,
            clientTypeName: c.clientType?.name || null,
        }));

    const employees = (data.employees || [])
        .filter((e) => e.id && e.active !== false)
        .map((e) => ({id: e.id, name: e.name || e.email || "Employé", email: e.email || ""}));

    const journals = (data.journals || []).map((j) => ({
        id: j.id,
        startDate: parseServerDate(j.startDate),
        endDate: parseServerDate(j.endDate),
        startTotal: Number(j.startTotal) || 0,
        startEmployeeId: j.startEmployee?.id || null,
        registerName: j.registerName || null,
        isOpen: !j.endDate,
    }));

    return {categories, products, payments, discounts, clients, employees, journals};
};

export const fetchCatalogue = async () => {
    const headers = await accountHeaders({"X-last-request": "2020-01-01T00:00:00+00:00"});
    const {data} = await api.get("/getCatalogue", {headers, timeout: 60000});
    return normalizeCatalogue(data);
};

// ── Journal (session de caisse) ─────────────────────────────────────────────

const journalFields = (j) => ({
    id: j.id,
    start_employee_id: `/api/employee/${j.startEmployeeId}`,
    end_employee_id: j.endEmployeeId ? `/api/employee/${j.endEmployeeId}` : null,
    is_open: String(!!j.isOpen),
    start_date: j.startDate,
    end_date: j.endDate || "",
    created_at: j.startDate,
    updated_at: Date.now(),
    start_total: String(Number(j.startTotal) || 0),
    end_total: String(Number(j.endTotal) || 0),
    register_name: WEB_REGISTER_NAME,
    active: "true",
});

export const openJournal = async ({employeeId, startTotal}) => {
    const journal = {
        id: newId(),
        startEmployeeId: employeeId,
        startDate: Date.now(),
        startTotal: Number(startTotal) || 0,
        isOpen: true,
        registerName: WEB_REGISTER_NAME,
    };
    await postSync("/journal/sync", journalFields(journal));
    return journal;
};

export const closeJournal = async ({journal, employeeId, endTotal}) => {
    await postSync("/journal/sync", journalFields({
        ...journal,
        isOpen: false,
        endEmployeeId: employeeId,
        endDate: Date.now(),
        endTotal: Number(endTotal) || 0,
    }));
};

// Espèces attendues en caisse : détail du journal (même endpoint que
// Activité > Journaux). Renvoie la réponse brute, ou null si indisponible.
export const fetchJournalDetails = async (journalId) => {
    try {
        const {data} = await api.get(`/journal/details/${journalId}`, {headers: await accountHeaders()});
        return data;
    } catch {
        return null;
    }
};

// ── Vente ───────────────────────────────────────────────────────────────────

// sale : {id, total, totalHt, totalRemise, note, clientId, employeeId, journalId}
// lines : [{id, productId, price, quantity, taxRate, note,
//           discounts: [{lineDiscountId, id, value, type}]}]
// payments : [{id, paymentId, amount}]
// Tous les ids sont fixés à la création (pas à l'envoi) : un nouvel essai
// après une coupure ré-upserte les mêmes lignes au lieu de les dupliquer.
// Envoi dans l'ordre vente → lignes → remises/paiements : les lignes
// référencent la vente, les remises référencent leur ligne.
export const submitSale = async ({sale, lines, payments}) => {
    const now = Date.now();
    await postSync("/sale/sync", {
        id: sale.id,
        status: SALE_STATUS_DONE,
        total: sale.total,
        total_ht: sale.totalHt,
        total_remise: sale.totalRemise,
        note: sale.note || null,
        active: "0",
        created_at: now,
        updated_at: now,
        employee_id_created: sale.employeeId,
        employee_id_updated: sale.employeeId,
        journal_id: sale.journalId,
        client_id: sale.clientId || null,
        device_name_creation: WEB_REGISTER_NAME,
        device_name_updated: WEB_REGISTER_NAME,
        is_refund: "0",
    });

    for (const line of lines) {
        // eslint-disable-next-line no-await-in-loop
        await postSync("/sale_items/sync", {
            id: line.id,
            sale_id: sale.id,
            product_id: line.productId,
            employee_id: sale.employeeId,
            price: line.price,
            quantity: line.quantity,
            taxe_value: line.taxRate ? Math.round(line.taxRate * 10000) / 100 : null,
            note: line.note || null,
            created_at: now,
            kitchen_printed: "0",
        });
    }

    const children = [];
    lines.forEach((line) => (line.discounts || []).forEach((d) => {
        children.push(postSync("/sale_discounts/sync", {
            id: d.lineDiscountId,
            sale_items_id: line.id,
            discount_id: d.id,
            amount: d.value,
            type: d.type || "%",
            employee_id: sale.employeeId,
            created_at: now,
        }));
    }));
    payments.forEach((p) => children.push(postSync("/sale_payments/sync", {
        id: p.id,
        sale_id: sale.id,
        payment_id: p.paymentId,
        amount: p.amount,
        created_at: now,
    })));
    await Promise.all(children);
    return sale.id;
};

// Facture PDF (même endpoint que la fiche client) → URL à ouvrir.
export const generateInvoice = async (saleId) => {
    const {data} = await api.get(`/generate_invoice/${saleId}`, {headers: await accountHeaders()});
    if (data?.status === "OK" && data?.url) return data;
    throw new Error("Erreur lors de la génération de la facture");
};
